export const DEFAULT_PUBLISHING_THEME_VERSION = 1;

export const PUBLISHING_THEME_COLOR_TOKENS = [
  'primary',
  'secondary',
  'accent',
  'background',
  'surface',
  'textPrimary',
  'textSecondary',
] as const;

export const PUBLISHING_THEME_FONT_TOKENS = [
  'heading',
  'body',
  'accent',
] as const;

export type PublishingThemeColorToken =
  (typeof PUBLISHING_THEME_COLOR_TOKENS)[number];
export type PublishingThemeFontToken =
  (typeof PUBLISHING_THEME_FONT_TOKENS)[number];

export type PublishingTheme = {
  version: number;
  colors: Record<PublishingThemeColorToken, string>;
  fonts: {
    heading: string;
    body: string;
    accent?: string;
  };
  spacing?: {
    baseUnit?: number;
  };
  cornerRadius?: {
    small?: number;
    medium?: number;
    large?: number;
  };
  effects?: {
    shadowStyle?: string;
  };
};

export type PublishingThemeOverrides = {
  colors?: Partial<PublishingTheme['colors']>;
  fonts?: Partial<PublishingTheme['fonts']>;
  spacing?: Partial<NonNullable<PublishingTheme['spacing']>>;
  cornerRadius?: Partial<NonNullable<PublishingTheme['cornerRadius']>>;
  effects?: Partial<NonNullable<PublishingTheme['effects']>>;
};

export const DEFAULT_PUBLISHING_THEME: PublishingTheme = {
  version: DEFAULT_PUBLISHING_THEME_VERSION,
  colors: {
    primary: '#7c3f00',
    secondary: '#c08457',
    accent: '#d4af37',
    background: '#fffaf5',
    surface: '#fffdf9',
    textPrimary: '#231f20',
    textSecondary: '#5c524d',
  },
  fonts: {
    heading: 'Playfair Display',
    body: 'Inter',
    accent: 'Cormorant Garamond',
  },
  spacing: {
    baseUnit: 8,
  },
  cornerRadius: {
    small: 8,
    medium: 20,
    large: 36,
  },
  effects: {
    shadowStyle: 'soft',
  },
};

function mergeNested<T extends Record<string, unknown>>(
  base: T,
  overrides?: Partial<T>,
): T {
  return {
    ...base,
    ...(overrides ?? {}),
  };
}

export function mergePublishingTheme(
  base: PublishingTheme,
  overrides?: PublishingThemeOverrides | null,
): PublishingTheme {
  if (!overrides) {
    return structuredClone(base);
  }

  return {
    version: base.version,
    colors: mergeNested(base.colors, overrides.colors),
    fonts: mergeNested(base.fonts, overrides.fonts),
    spacing: mergeNested(base.spacing ?? {}, overrides.spacing),
    cornerRadius: mergeNested(base.cornerRadius ?? {}, overrides.cornerRadius),
    effects: mergeNested(base.effects ?? {}, overrides.effects),
  };
}

export function createPublishingTheme(
  input?: Partial<PublishingTheme> | null,
): PublishingTheme {
  if (!input) {
    return structuredClone(DEFAULT_PUBLISHING_THEME);
  }

  return {
    version: input.version ?? DEFAULT_PUBLISHING_THEME.version,
    colors: mergeNested(DEFAULT_PUBLISHING_THEME.colors, input.colors),
    fonts: mergeNested(DEFAULT_PUBLISHING_THEME.fonts, input.fonts),
    spacing: mergeNested(DEFAULT_PUBLISHING_THEME.spacing ?? {}, input.spacing),
    cornerRadius: mergeNested(
      DEFAULT_PUBLISHING_THEME.cornerRadius ?? {},
      input.cornerRadius,
    ),
    effects: mergeNested(DEFAULT_PUBLISHING_THEME.effects ?? {}, input.effects),
  };
}
